import { useAtom } from '@reatom/npm-react'
import { useDrag } from '@use-gesture/react'
import {
  motion,
  useMotionTemplate,
  useMotionValue,
  usePresence,
} from 'motion/react'
import { useEffect, useState } from 'react'
import styled from 'styled-components'

import { Button } from '@blade/ui-kit'
import { Gear2Icon, WalletIcon } from '@root/shared/ui-kit'
import { favoriteDrawerOpenAtom } from '@root/widgets/desktop/favorite-drawer'

import { instantTradeDialogVariantAtom } from '../../atoms'

import { InstantTradeBottomBuy } from './instant-trade-bottom-buy'
import { InstantTradeBottomSell } from './instant-trade-bottom-sell'
import { InstantTradeBottomSettings } from './instant-trade-bottom-settings'
import { InstantTradeBottomWallets } from './instant-trade-bottom-wallets'

import type { PairModel } from '@root/entities/pair'
import type { ClassNameProps } from '@root/shared/react'

const THRESHOLD = 100

type InstantTradeBottomDialogProps = {
  pair: PairModel
} & ClassNameProps

export const InstantTradeBottomDialog: React.FC<
  InstantTradeBottomDialogProps
> = ({ className, pair }) => {
  const [isDragging, setIsDragging] = useState(false)
  const [_, setVariant] = useAtom(instantTradeDialogVariantAtom)
  const [isPresent, safeToRemove] = usePresence()
  const [favoriteDrawerOpen] = useAtom(favoriteDrawerOpenAtom)

  const opacity = useMotionValue(isPresent ? 1 : 0)
  const yValue = useMotionValue(0)
  const isDraggingValue = useMotionValue(false)

  const bind = useDrag(
    (state) => {
      if (state.target instanceof HTMLInputElement) {
        return
      }

      isDraggingValue.set(state.dragging ?? false)
      setIsDragging(state.dragging ?? false)
      const offset = [state.movement[0], state.movement[1]] as const

      if (offset[1] > 0) {
        return
      }

      yValue.set(offset[1])
      const opacityValue = 1 - Math.abs(offset[1]) / (THRESHOLD * 2)
      opacity.set(opacityValue)

      if (!state.dragging) {
        const isPassedThreshold = Math.abs(offset[1]) > THRESHOLD
        if (isPassedThreshold) {
          setVariant('floating')
        } else {
          yValue.set(1)
          yValue.set(0)
        }
      }
    },
    {
      filterTaps: true,
      // True will interrupt input focus
      preventDefault: false,
      axis: 'y',
    },
  )

  useEffect(() => {
    if (!isPresent) {
      opacity.set(0)
      setTimeout(safeToRemove, 200)
    }
  }, [isPresent, opacity, safeToRemove])

  const transform = useMotionTemplate`translateY(${yValue}px`

  return (
    <>
      <Root
        className={className}
        style={{
          opacity,
          transform,
          bottom: favoriteDrawerOpen ? '-100px' : undefined,
        }}
        {...(bind() as {})}
      >
        <InstantTradeBottomBuy pair={pair} />
        <Separator />
        <InstantTradeBottomSell pair={pair} />
        <Actions>
          <InstantTradeBottomWallets pair={pair} type="wallets">
            <Button
              radius="rounded"
              variant="secondary"
              size="h36"
              iconLeft={<WalletIcon />}
            />
          </InstantTradeBottomWallets>
          <InstantTradeBottomSettings>
            <Button
              radius="rounded"
              variant="secondary"
              size="h36"
              iconLeft={<Gear2Icon />}
            />
          </InstantTradeBottomSettings>
        </Actions>
      </Root>
      <BottomHighlight
        style={{
          opacity: isDragging ? opacity : 0,
        }}
      />
    </>
  )
}

const Root = styled(motion.div)`
  cursor: grab;

  border-radius: 10px;
  border: 1px solid var(--backgroundA-3);
  background: var(--backgroundA-2);
  backdrop-filter: blur(60px);
  touch-action: none;

  height: 53px;
  position: absolute;

  bottom: 30px;
  width: calc(100vw - 63px);
  padding-inline: 0 8px;
  left: 55px;

  opacity: 0;

  display: grid;
  grid-auto-flow: column;
  justify-content: space-between;
  align-items: center;
`

const Separator = styled.div`
  width: 1px;
  height: 45px;
  background: var(--backgroundA-3);
`

const Actions = styled.div`
  display: grid;
  grid-auto-flow: column;
  gap: 2px;

  border-radius: 9px;
  background: var(--backgroundA-2);
  padding: 2px;
`

const BottomHighlight = styled(motion.div)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 94px;
  background: linear-gradient(180deg, transparent, var(--primaryA-3));
  pointer-events: none;
  border-bottom: 2px solid var(--primaryA-9);

  opacity: 0;
  transition: opacity 0.2s ease-in-out;
`
